import React, { type JSX, type ReactNode } from 'react';
import { type ErrorResponse } from '@soulhx/fs-common';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { type ApiErrorType } from '../dm/errors.js';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { type SerializedError } from '@reduxjs/toolkit';
import { Alert } from 'react-bootstrap';

interface IFormattedCustomError {
  er: ErrorResponse;
}
export const FormattedCustomError = ({ er }: IFormattedCustomError) => {
  const { t } = useTranslation(['uicommon']);

  return (
    <div key={uuid()}>
      <p>{t('uicommon:errorMessages.formattedCustom.title', { code: er.errorCode })}</p>
      <ul>
        {er.errors.map((item) => (
          <li key={`err-msg-detail-${uuid()}`}>{`${item.message} ${item.field ? '; ' + item.field : ''}`}</li>
        ))}
      </ul>
    </div>
  );
};

interface IErrorLoadingDataMessage {
  errors: ApiErrorType[];
}
export const ErrorLoadingDataMessage = ({ errors }: IErrorLoadingDataMessage) => {
  const { t } = useTranslation(['uicommon']);
  const messages: JSX.Element[] = [];

  for (const err of errors) {
    if (!err) continue;

    // both ErrorResponse and FetchBaseQuery have 'data' properties
    if ('data' in err) {
      if (typeof err.data === 'object' && err.data !== null && 'errorCode' in err.data && 'errors' in err.data) {
        messages.push(
          <div key={uuid()}>
            <FormattedCustomError er={err.data as ErrorResponse} />
          </div>
        );
      } else if ('status' in err) {
        messages.push(
          <div key={uuid()}>
            {t('uicommon:errorMessages.errorLoadingData.statusCode', {
              statusCode: (err as FetchBaseQueryError).status,
            })}
          </div>
        );
      }
    } else if ('message' in err) {
      messages.push(<div key={uuid()}>{(err as Error).message}</div>);
    } else {
      messages.push(
        <div key={uuid()}>
          {(err as SerializedError).message || t('uicommon:errorMessages.errorLoadingData.noData')}
        </div>
      );
    }
  }

  return <Alert variant="danger">{messages}</Alert>;
};

interface IUknownServerError {
  /**
   * The error being handled
   */
  er: unknown;

  /**
   * A fallback string to use if there's no good way to handle this error
   */
  fallback: string;
}
/**
 * Used for handling an error raised from a server call. Could be a
 * standard SoulHX `ErrorResponse` or it could be something else like
 * an **RTK query** error -- or it could be something we can't even
 * handle, in which case we'll just display a fallback message.
 */
export const DisplayUnknownError = ({ er, fallback }: IUknownServerError) => {
  if (typeof er !== 'object' || er === null) {
    return <div>{fallback}</div>;
  }

  if ('status' in er || 'data' in er || 'message' in er) {
    return <ErrorLoadingDataMessage errors={[er as ApiErrorType]} />;
  }

  return <div>{fallback}</div>;
};

interface IClientSideError {
  children: ReactNode;
}
export const ClientSideErrorLoading = ({ children }: IClientSideError) => {
  const { t } = useTranslation(['uicommon']);

  return (
    <Alert variant="danger">
      <Alert.Heading>{t('uicommon:errorMessages.clientSide.title')}</Alert.Heading>
      <div>{t('uicommon:errorMessages.clientSide.message')}</div>
      <div>{children}</div>
    </Alert>
  );
};
